import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ordersApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Order } from '../types';
import './OrderDetailsPage.css';

export const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (id) {
      loadOrder(Number(id));
    }
  }, [id]);

  useEffect(() => {
    // Show back button
    const backHandler = () => {
      navigate(-1);
    };

    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(backHandler);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(backHandler);
      }
    };
  }, [tg, navigate]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersApi.getOrder(orderId);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить заказ');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      pending: 'Ожидает оплаты',
      paid: 'Оплачен',
      preparing: 'Собирается',
      delivering: 'В доставке',
      completed: 'Завершен',
      cancelled: 'Отменен',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      pending: 'status-pending',
      paid: 'status-paid',
      preparing: 'status-preparing',
      delivering: 'status-delivering',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return colorMap[status] || '';
  };

  const canCancelOrder = (status: string) => {
    return status === 'pending' || status === 'paid';
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    if (!canCancelOrder(order.status)) {
      tg?.showAlert('Этот заказ нельзя отменить');
      return;
    }

    tg?.showConfirm('Вы уверены, что хотите отменить заказ?', async (confirmed: boolean) => {
      if (!confirmed) return;

      try {
        setCancelling(true);
        await ordersApi.cancelOrder(order.id);

        // Reload order to get updated status
        if (id) {
          await loadOrder(Number(id));
        }

        tg?.showAlert('Заказ отменен');
      } catch (err: unknown) {
        tg?.showAlert(err instanceof Error ? err.message : 'Ошибка отмены заказа');
      } finally {
        setCancelling(false);
      }
    });
  };

  if (loading) {
    return (
      <div className="page order-details-page">
        <div className="order-details-header">
          <button className="back-btn" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="order-details-title">Заказ</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="page order-details-page">
        <div className="order-details-header">
          <button className="back-btn" onClick={handleBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="order-details-title">Заказ</h1>
        </div>
        <div className="error-container">
          <p className="error-message">{error || 'Заказ не найден'}</p>
          <button className="retry-button" onClick={handleBack}>
            Вернуться назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page order-details-page">
      <div className="order-details-header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="order-details-title">Заказ #{order.id}</h1>
      </div>

      <div className="order-details-content">
        {/* Status Card */}
        <div className="details-card">
          <div className="status-section">
            <div className={`order-status-large ${getStatusColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </div>
            <p className="order-date">{formatDate(order.created_at)}</p>
          </div>
        </div>

        {/* Items Card */}
        {order.items && order.items.length > 0 && (
          <div className="details-card">
            <h2 className="card-title">Товары</h2>
            <div className="order-items-list">
              {order.items.map((item) => (
              <div key={item.id} className="order-item-detail">
                {item.image_url && (
                  <img
                    src={item.image_url}
                    alt={item.product_name}
                    className="order-item-detail-image"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/80x80?text=No+Image';
                    }}
                  />
                )}
                <div className="order-item-detail-info">
                  <h3 className="order-item-detail-name">{item.product_name}</h3>
                  <div className="order-item-detail-meta">
                    <span className="order-item-detail-price">{formatPrice(item.price)}</span>
                    <span className="order-item-detail-quantity">×{item.quantity}</span>
                  </div>
                </div>
                <div className="order-item-detail-subtotal">
                  {formatPrice(item.subtotal)}
                </div>
              </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery Address Card */}
        {order.delivery_address && (
          <div className="details-card">
            <h2 className="card-title">Адрес доставки</h2>
            <div className="delivery-address">
              {'address_line' in order.delivery_address && order.delivery_address.address_line && (
                <p className="address-text">{order.delivery_address.address_line}</p>
              )}
              {'city' in order.delivery_address && order.delivery_address.city && (
                <p className="address-detail">{order.delivery_address.city}</p>
              )}
              {'postal_code' in order.delivery_address && order.delivery_address.postal_code && (
                <p className="address-detail">Индекс: {order.delivery_address.postal_code}</p>
              )}
              {order.delivery_address.notes && (
                <p className="address-detail">Примечание: {order.delivery_address.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Notes Card */}
        {order.notes && (
          <div className="details-card">
            <h2 className="card-title">Комментарий</h2>
            <p className="order-notes">{order.notes}</p>
          </div>
        )}

        {/* Total Card */}
        <div className="details-card">
          <h2 className="card-title">Итого</h2>
          <div className="order-summary">
            <div className="summary-row">
              <span className="summary-label">Товары{order.items ? ` (${order.items.length})` : ''}:</span>
              <span className="summary-value">{formatPrice(order.total_amount - order.delivery_fee)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Доставка:</span>
              <span className="summary-value">{formatPrice(order.delivery_fee)}</span>
            </div>
            <div className="summary-row summary-total">
              <span className="summary-label">Итого:</span>
              <span className="summary-value">
                {formatPrice(order.total_amount)}
              </span>
            </div>
          </div>
        </div>

        {/* Status History */}
        {order.status_history && order.status_history.length > 0 && (
          <div className="details-card">
            <h2 className="card-title">История статусов</h2>
            <div className="status-history">
              {order.status_history.map((history) => (
                <div key={history.id} className="status-history-item">
                  <div className="status-history-dot"></div>
                  <div className="status-history-content">
                    <div className="status-history-status">
                      {getStatusLabel(history.status)}
                    </div>
                    <div className="status-history-date">
                      {formatDate(history.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel Order Button */}
        {canCancelOrder(order.status) && (
          <div className="cancel-order-section">
            <button
              className="cancel-order-button"
              onClick={handleCancelOrder}
              disabled={cancelling}
            >
              {cancelling ? 'Отмена...' : 'Отменить заказ'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
